import { ManagedAccount, ManagedAccountBrand } from "../../store/agency/types";

/** Truncate UUID to first 8 chars for display */
export const truncateId = (id: string): string =>
  id.length > 8 ? `${id.slice(0, 8)}…` : id;

/** Get display name: label → email → truncated UUID */
export const getDisplayName = (client: ManagedAccount): string =>
  client.label || client.clientEmail || `Client ${truncateId(client.clientAccountId)}`;

/** Get avatar initial from email/label or fallback to "C" */
export const getAvatarInitial = (client: ManagedAccount): string => {
  const name = client.clientEmail || client.label;
  return name ? name.charAt(0).toUpperCase() : "C";
};

/** Format date as "DD MMM YYYY" */
export const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export type ScoreTrendDirection = "up" | "down" | "flat" | "empty";

export interface FormattedScoreTrend {
  text: string;
  direction: ScoreTrendDirection;
}

/** Format visibility score for display. */
export const formatScore = (score: number | null): string =>
  score !== null ? String(score) : "—";

/** Format week-over-week score trend without duplicated dash placeholders. */
export const formatScoreTrend = (trend: number | null): FormattedScoreTrend => {
  if (trend === null) {
    return { text: "—", direction: "empty" };
  }
  if (trend === 0) {
    return { text: "0", direction: "flat" };
  }
  if (trend > 0) {
    return { text: `▲ +${trend}`, direction: "up" };
  }
  return { text: `▼ ${trend}`, direction: "down" };
};

/** Show email under client name only when it adds information beyond the primary label. */
export const shouldShowClientEmail = (client: ManagedAccount): boolean =>
  Boolean(client.label && client.clientEmail && client.clientEmail !== client.label);

/** Brands list from API; empty when field is missing. */
export const getClientBrands = (client: ManagedAccount): ManagedAccountBrand[] =>
  client.brands ?? [];

/** Relative time label for updated-at timestamps. */
export const formatTimeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "< 1h ago";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};
